"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_LAB_PERMISSIONS = exports.LabPermissions = void 0;
/**
 * Laboratory (LIS) permission catalog — ADR-087 Phase 2, extended by
 * ADR-088 Phase 9B.
 *
 * TWENTY-FIVE permissions: twelve MASTER-DATA grants and thirteen WORKFLOW
 * grants. The split is the point. Phase 9 shipped with the workflow mapped
 * onto `lab:master:edit` / `lab:master:approve`, which meant anyone who could
 * rename a test could also enter and verify a patient's result. That is not
 * acceptable in a LIS, and it is what this extension corrects.
 *
 * THE COUNT IS STILL A CONSTRAINT. The JWT embeds EVERY permission a user holds, and this
 * platform has hit two separate header ceilings weeks apart — Node's 16KB
 * limit (→ 431) and nginx's `large_client_header_buffers` (→ 400, with a
 * silent backend log). There must never be per-discipline or per-test
 * grants in this family.
 *
 * The separations that matter, and why each exists:
 *   - `lab:template:publish` is NOT implied by `lab:master:edit`. Publishing
 *     a report template is a clinical act that decides what a patient's
 *     result page says; editing a test code is not.
 *   - `lab:master:approve` is NOT implied by `lab:master:import`. The
 *     import pipeline enforces `approvedBy != uploadedBy` per job, which is
 *     only meaningful if the two grants can be held separately.
 *   - reference ranges and critical values carry their own edit grants,
 *     because "who may rename a test" and "who may decide what counts as a
 *     critical potassium" are not the same person.
 *
 * REGISTRATION IS TWO EDITS, NOT ONE. Adding this catalog means adding
 * `PermissionService.seedLabCatalog()` AND its name to
 * `scripts/deploy-seed-all-catalogs.ts`'s CATALOG_SEEDERS list. A family
 * that exists here but is never seeded produces routes that 403 for every
 * user including SuperAdmin — the ADR-085 Phase 0 deploy shipped eleven
 * /admissions routes exactly that way.
 *
 * The pre-existing `test-master:*` permissions in `master-data.permissions.ts`
 * are deliberately NOT replaced — the legacy `/api/v1/test-masters` surface
 * keeps working unchanged (ADR-087 D-14).
 */
exports.LabPermissions = {
    LAB_MASTER_VIEW: 'lab:master:view',
    LAB_MASTER_CREATE: 'lab:master:create',
    LAB_MASTER_EDIT: 'lab:master:edit',
    LAB_MASTER_IMPORT: 'lab:master:import',
    LAB_MASTER_APPROVE: 'lab:master:approve',
    LAB_TEMPLATE_VIEW: 'lab:template:view',
    LAB_TEMPLATE_CREATE: 'lab:template:create',
    LAB_TEMPLATE_EDIT: 'lab:template:edit',
    LAB_TEMPLATE_PUBLISH: 'lab:template:publish',
    LAB_REFERENCE_RANGE_EDIT: 'lab:reference-range:edit',
    LAB_CRITICAL_VALUE_EDIT: 'lab:critical-value:edit',
    LAB_CODE_MAPPING: 'lab:code-mapping:edit',
    // --- ADR-088 Phase 9B: WORKFLOW grants (operational, not master data) ---
    //
    // THIRTEEN, not the fifteen originally proposed. Dropped: `lab:order:view`,
    // `lab:specimen:view` and `lab:result:view`, folded into one
    // `lab:workflow:view`. Those three are never separable in practice — every
    // worklist screen shows an order, its specimens and its results together, so
    // granting one without the others produces a configuration nobody can
    // operate, at the cost of three more strings in every token.
    //
    // `lab:report:view` IS kept separate, because report readership is genuinely
    // a different population: a ward nurse may need to read a patient's report
    // without any access to the lab's internal worklists.
    /** Read the whole workflow: orders, specimens, results, worklists, queues. */
    LAB_WORKFLOW_VIEW: 'lab:workflow:view',
    LAB_ORDER_CREATE: 'lab:order:create',
    /** Separate from create: cancelling destroys accepted work. */
    LAB_ORDER_CANCEL: 'lab:order:cancel',
    LAB_SPECIMEN_COLLECT: 'lab:specimen:collect',
    /**
     * Separate from collect. Receiving is the lab accepting CUSTODY; if a
     * phlebotomist could mark specimens received, a tube could appear in the
     * lab's queue without ever arriving.
     */
    LAB_SPECIMEN_RECEIVE: 'lab:specimen:receive',
    /** A quality decision that invalidates collected work. */
    LAB_SPECIMEN_REJECT: 'lab:specimen:reject',
    LAB_RESULT_ENTER: 'lab:result:enter',
    /** Separate from enter: an amendment changes already-released clinical data. */
    LAB_RESULT_AMEND: 'lab:result:amend',
    /**
     * The two verification levels carry SEPARATE grants. The model already
     * orders them (technical before clinical); if one permission covered both,
     * that ordering would be a formality rather than a control, and the
     * separation-of-duties check would be the only thing standing between a
     * single operator and a released result.
     */
    LAB_RESULT_VERIFY_TECHNICAL: 'lab:result:verify:technical',
    LAB_RESULT_VERIFY_CLINICAL: 'lab:result:verify:clinical',
    /**
     * ADR-098 — the third QC pass, after clinical.
     *
     * A separate grant rather than a widening of `verify:clinical`, so the two
     * signatures can be held by different people. SuperAdmin receives it
     * automatically: `deploy-seed-all-catalogs` grants every permission to every
     * `isSystemRole` role, and SuperAdmin is the only one.
     */
    LAB_RESULT_VERIFY_QC3: 'lab:result:verify:qc3',
    /** Reading a patient-facing report — a wider audience than lab staff. */
    LAB_REPORT_VIEW: 'lab:report:view',
    /** Releasing a patient-facing clinical artifact. */
    LAB_REPORT_FINALIZE: 'lab:report:finalize',
    LAB_REPORT_AMEND: 'lab:report:amend',
};
exports.ALL_LAB_PERMISSIONS = Object.values(exports.LabPermissions);
//# sourceMappingURL=lab.permissions.js.map